import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { blogPosts } from '../data/blog';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi'; // Importing the arrow icon

const BlogCard = ({ id, image, category, title, date, excerpt }) => {
  const navigate = useNavigate();

  return (
    <article
      onClick={() => navigate(`/blog/${id}`)}
      className="group cursor-pointer bg-white rounded-2xl overflow-hidden border border-transparent hover:border-slate-100 hover:shadow-xl transition-all duration-300"
    >
      <div className="relative overflow-hidden aspect-video bg-slate-100">
        <img 
          src={image} 
          alt={`${title} - ${category} Insights`}
          loading="lazy"
          className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-500"
        />
        <span className="absolute top-4 left-4 bg-[#0054A6] text-white text-[10px] font-black px-3 py-1 rounded-full uppercase">
          {category}
        </span>
      </div>
      
      <div className="p-5">
        <time dateTime={date} className="text-slate-400 text-[10px] font-bold mb-2 uppercase tracking-tighter block">
          {date}
        </time>
        <h2 className="text-xl font-bold text-slate-800 group-hover:text-[#0054A6] transition-colors leading-tight mb-3">
          {title}
        </h2>
        <p className="text-slate-500 text-sm line-clamp-3 leading-relaxed">
          {excerpt}
        </p>
        
        <div className="mt-4 flex items-center gap-2 text-[#EE3124] font-black text-[10px] uppercase tracking-widest">
          Read Full Report <span className="sr-only">about {title}</span> →
        </div>
      </div>
    </article>
  );
};

const BlogPage = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('All Posts');

  const filteredPosts = activeCategory === 'All Posts' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === activeCategory);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Industry Insights | Protech Construction Petroleum Blog</title>
        <meta name="description" content="Expert analysis on petroleum infrastructure, maintenance, and construction innovation by Protech Construction." />
        <meta property="og:title" content="Industry Insights | Protech Construction" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://protech.construction/blog" />
      </Helmet>

      {/* STICKY MINI NAV (Synced with Terms/Privacy pages) */}
      <nav className="p-6 bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <button 
            onClick={() => navigate("/")}
            className="text-[#0054A6] font-bold flex items-center gap-2 hover:text-[#EE3124] transition-all group"
          >
            <FiArrowLeft className="group-hover:-translate-x-1 transition-transform" /> 
            Back to Home
          </button>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
            <span className="font-black text-slate-800 uppercase tracking-widest text-[10px]">Information Portal</span>
          </div>
        </div>
      </nav>

      <header className="bg-slate-50 border-b border-slate-100 px-6 py-16 lg:py-24">
        <div className="max-w-6xl mx-auto">
          <nav className="mb-4 text-xs font-bold text-slate-400 uppercase tracking-widest">
            Home / Blog {activeCategory !== 'All Posts' && `/ ${activeCategory}`}
          </nav>
          <h1 className="text-4xl lg:text-6xl font-black text-[#0054A6]">
            Industry <span className="text-[#EE3124]">Insights.</span>
          </h1>
          <p className="text-slate-600 mt-6 text-lg max-w-2xl leading-relaxed">
            Stay updated with the latest trends in **Petroleum infrastructure**, **Retail Outlet maintenance**, and **Construction technology**.
          </p>
        </div>
      </header>

      <main className="px-6 py-16 lg:py-20">
        <div className="max-w-6xl mx-auto">
          
          {/* Category Filter Navigation */}
          <nav aria-label="Blog categories" className="flex gap-4 overflow-x-auto pb-8 mb-12 border-b border-slate-100 scrollbar-hide">
            {['All Posts', 'Maintenance', 'Safety', 'Innovation'].map((cat) => (
              <button 
                key={cat}
                onClick={() => setActiveCategory(cat)}
                aria-pressed={activeCategory === cat}
                className={`whitespace-nowrap px-6 py-2 rounded-full text-sm font-bold transition-all ${
                  activeCategory === cat 
                  ? 'bg-[#0054A6] text-white shadow-lg shadow-blue-100' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </nav>

          {/* Dynamic Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
            {filteredPosts.length > 0 ? (
              filteredPosts.map((post) => (
                <BlogCard key={post.id} {...post} />
              ))
            ) : (
              <p className="text-slate-400 italic">No industry reports found in this category.</p>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default BlogPage;